import { messages } from './errors'
import { isString, isBoolean, isObject, isArray } from '../utils/type-utils'
import { RejectingValidator } from './base-validator'

class ApiValidator extends RejectingValidator {
  arity(method: string, args: IArguments, from: number, to: number) {
    return this.addValidation(
      () => args.length <= to && args.length >= from,
      () => messages.invalidArgumentLength(method, from, to, args.length)
    )
  }

  _isObject(item: any, collectionName: string) {
    return this.addValidation(
      () => isObject(item),
      () => messages.itemMustBeAnObject(item, collectionName)
    )
  }

  item(item: any, collectionName: string, requireId: boolean) {
    return this._isObject(item, collectionName).addValidation(
      () => (item._id !== undefined ? isString(item._id) : !requireId),
      () => messages.updateItemInvalid()
    )
  }

  items(items: any, collectionName: string) {
    return this.addValidation(
      () =>
        isArray(items) &&
        items.every(
          (item) =>
            isObject(item) &&
            (item._id === undefined || item._id === null || isString(item._id))
        ),
      () => messages.itemsMustBeArrayOfObjects(collectionName)
    ).addValidation(
      () => items.length <= 1000,
      () => messages.itemsMustBeLessThanThousand(collectionName)
    )
  }

  fieldName(fieldName: any) {
    return this.addValidation(
      () => isString(fieldName),
      () => messages.fieldNameMustBeAString()
    )
  }

  itemId(itemId: any) {
    return this.addValidation(
      () => isString(itemId),
      () => messages.itemIdMustBeAString()
    )
  }

  itemIds(itemIds: any, collectionName: string) {
    return this.addValidation(
      () => isArray(itemIds) && itemIds.every((itemId) => isString(itemId)),
      () => messages.itemIdsMustBeArrayOfStrings()
    ).addValidation(
      () => itemIds.length <= 1000,
      () => messages.removeItemsMustBeLessThanThousand(collectionName)
    )
  }

  _options(options: any, flags: string[]) {
    return this.addValidation(
      () => {
        const validOptions =
          isObject(options) &&
          flags.every((flag) => {
            if (flag === 'language') {
              return isNonEmptyStringIfDefined(options[flag])
            } else {
              return isBooleanIfDefined(options[flag])
            }
          })
        return options == null || validOptions
      },
      () => messages.optionsInvalid(flags)
    )
  }

  referenceRemoveParameters(parameters: any) {
    return this.addValidation(
      () => isArray(parameters) && parameters.every(isRemoveReferenceParameter),
      messages.referenceOperationParameterError
    )
  }

  referenceParameters(parameters: any) {
    return this.addValidation(
      () => isArray(parameters) && parameters.every(isReferenceParameter),
      messages.referenceOperationParameterError
    )
  }

  referenceParameter(parameter: any) {
    return this.addValidation(
      () => isReferenceParameter(parameter),
      messages.referenceOperationParameterError
    )
  }

  isNonEmptyString(operand: any, operandName: string) {
    return this.addValidation(
      () => typeof operand === 'string' && operand.length > 0,
      () => messages.queryValidations.isNonEmptyString(operandName)
    )
  }

  references(refs: any) {
    return this.addValidation(
      () =>
        isArray(refs) &&
        refs.every(
          (ref) =>
            isString(ref.relationshipName) &&
            isReferenceParameter(ref.left) &&
            isReferenceParameter(ref.right)
        ),
      messages.invalidReferenceError
    )
  }

  options(options: any) {
    return this._options(options, [
      'suppressAuth',
      'suppressHooks',
      'showDrafts',
      'consistentRead',
      'language',
    ])
  }

  bulkInsertOptions(options: any) {
    return this._options(options, [
      'suppressAuth',
      'suppressHooks',
      'overrideExisting',
    ])
  }

  bulkUpdateOptions(options: any) {
    return this._options(options, [
      'suppressAuth',
      'suppressHooks',
      'showDrafts',
    ])
  }

  bulkRemoveOptions(options: any) {
    return this._options(options, [
      'suppressAuth',
      'suppressHooks',
      'showDrafts',
    ])
  }

  truncateOptions(options: any) {
    return this._options(options, ['suppressAuth'])
  }

  collectionName(collectionName: any) {
    return this.addValidation(
      () => isString(collectionName),
      () => messages.collectionNameMustBeAString()
    )
  }
}

export function apiValidator() {
  return new ApiValidator()
}

function isNonEmptyStringIfDefined(value: any) {
  return value == null || (isString(value) && value.length > 0)
}

function isBooleanIfDefined(value: any) {
  return value == null || isBoolean(value)
}

function isReferenceParameter(parameter: any) {
  return (
    (isString(parameter) && parameter !== '') ||
    (isObject(parameter) && parameter.hasOwnProperty('_id'))
  )
}

function isRemoveReferenceParameter(parameter: any) {
  return (
    isString(parameter) ||
    (isObject(parameter) && parameter.hasOwnProperty('_id'))
  )
}
